<template>
  <div class="page">
    <Banner v-if="viewModel.disabled">
      <div class="banner-text">Bots are currently disabled</div>
      <n-button @click="enableBot" variant="primary" :loading="enableInProgress">Enable </n-button>
    </Banner>

    <Banner v-if="botAppInstallError" :show-overlay="true">
      <div class="banner-text">There's a problem with your bot config. Let's set it up again</div>
      <n-button @click="reinstall" variant="primary" :loading="reinstallInProgress"> Reconfigure </n-button>
    </Banner>

    <n-loading-spinner v-if="!botAppLoaded"></n-loading-spinner>

    <section class="section content-container" v-if="botAppLoaded">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column">
            <TitledSection class="container island is-fluid">
              <template v-slot:icon>
                <TriageIcon></TriageIcon>
              </template>
              <template v-slot:title>Triage Contacts</template>
              <template v-slot:subtitle>Decide what data to capture and actions to take.</template>
              <div class="columns triage-contacts">
                <div class="column">
                  <div class="intro-message">Attendant will triage contacts by asking for more information</div>
                  <div class="contact-information-container">
                    <div class="contact-information">
                      <n-toggle id="capture-name" v-model="viewModel.captureName" label="Contact Name"></n-toggle>
                    </div>

                    <div class="contact-information">
                      <n-toggle id="capture-email" v-model="viewModel.captureEmail" label="Email Address"></n-toggle>
                    </div>

                    <div class="contact-information">
                      <n-toggle
                        id="capture-enquiry"
                        v-model="viewModel.captureEnquiry"
                        label="Enquiry Topic"
                      ></n-toggle>
                    </div>

                    <div class="contact-information" v-if="viewModel.captureEnquiry">
                      <span class="contact-attribute-label">Topics</span>
                      <TagsInput
                        class="topic-tags"
                        element-id="enquiry-topics"
                        placeholder="Enter your topic..."
                        v-model="viewModel.enquiryOptions"
                        :disabled="!viewModel.captureEnquiry"
                      ></TagsInput>
                    </div>
                  </div>

                  <RulesList
                    ref="rulesList"
                    :configRequest="configRequest"
                    :selected-apps="getSelectedFollowUpApps()"
                    :rules="viewModel.rules"
                    :rule-conditions-manager="ruleConditionsManager"
                    @rules-updated="rulesUpdated"
                    @followup-action-change="selectedFollowUpActionsChanged"
                  />
                </div>
              </div>
            </TitledSection>
          </div>
        </div>
        <div class="save-bot column">
          <n-button @click="save()" variant="primary" class="save-button" :loading="updateInProgress">Save </n-button>
          <a v-if="!viewModel.disabled && !botAppInstallError" @click="disableBot">I don't want to use bots...</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { ApiError } from '@/shared/services/baseApiClient';
import Toast from '@/shared/services/toast';
import TitledSection from '@/shared/components/TitledSection.vue';
import Banner from '@/shared/components/Banner.vue';
import { botsApi } from '../services/bots';
import { botAppSerivce } from '../services/botAppService';
import {
  BotActionAppViewModel,
  BotConfigurationViewModel,
  FollowUpActionType,
  FollowUpActionViewModel,
} from '@/features/bots/models';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import TagsInput from '@/shared/components/TagsInput.vue';
import TriageIcon from '@/shared/components/TriageIcon.vue';
import { ConfiguredAppIntegration } from '../../apps/shared/models/configuredAppIntegration';
import { RuleViewModel } from '../models/ruleViewModel';
import RulesList from '@/features/bots/components/rules/RulesList.vue';
import { ConditionDataType } from '@/shared/components/conditions/models/conditions';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { useRuleConditions } from '@/features/bots/components/rules/useRuleConditions';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const botAppLoaded = ref(false);
const botAppInstallError = ref(false);
const updateInProgress = ref(false);
const reinstallInProgress = ref(false);
const enableInProgress = ref(false);

const rulesList = ref<typeof RulesList | null>(null);

const viewModel = ref<BotConfigurationViewModel>({
  disabled: false,
  captureName: true,
  captureEmail: true,
  captureEnquiry: false,
  enquiryOptions: [],
  rules: [],
  followUpActions: [],
});

const ruleConditionsManager = useRuleConditions([
  {
    id: 'flow.route',
    title: 'Enquiry Topic',
    type: ConditionDataType.Text,
    icon: 'QuestionIcon',
  },
  {
    id: 'appUser.currentUrl',
    title: 'Current Page URL',
    type: ConditionDataType.Text,
    icon: 'PageIcon',
  },
]);

onBeforeMount(() => {
  if (!currentWorkspace.value) {
    handleSeriousError('No workspace selected');
    return;
  }

  botsApi
    .get(currentWorkspace.value.id)
    .then((botResponse: BotConfigurationViewModel) => {
      initBotApp(botResponse);
      botAppLoaded.value = true;
    })
    .catch((error: ApiError) => {
      if (error.status === 404) {
        botAppInstallError.value = true;
        botAppLoaded.value = true;
        return;
      }
      handleSeriousError(error.message);
    });
});

function configRequest(
  workspaceId: string,
  installedAppId: string,
  values: any,
  previousValues: any,
  componentId: any,
) {
  return botsApi.botAppConfig(workspaceId, installedAppId, values, previousValues, componentId);
}

function getSelectedFollowUpApps(): Array<ConfiguredAppIntegration | undefined> {
  return viewModel.value.followUpActions
    .filter((action: FollowUpActionViewModel) => action.type === FollowUpActionType.AppIntegration)
    .filter((action: FollowUpActionViewModel) => action.app !== undefined)
    .map((action: FollowUpActionViewModel) => action.app);
}

function rulesUpdated(rules: RuleViewModel[]) {
  viewModel.value.rules = rules;
}

function selectedFollowUpActionsChanged(selectedApps: BotActionAppViewModel[]) {
  const nonAppActions = viewModel.value.followUpActions.filter(
    (x: FollowUpActionViewModel) => x.type !== FollowUpActionType.AppIntegration,
  );
  const appActions = selectedApps.map<FollowUpActionViewModel>((app) => ({
    type: FollowUpActionType.AppIntegration,
    app,
  }));
  viewModel.value.followUpActions = [...appActions, ...nonAppActions];
}

function reinstall() {
  if (reinstallInProgress.value) {
    return;
  }

  reinstallInProgress.value = true;

  botAppSerivce
    .fullInstall(currentWorkspace.value.id)
    .then((res) => {
      initBotApp(res);
      Toast.showSuccess('Bots have been successfully configured');
      botAppInstallError.value = false;
    })
    .catch((error) => {
      Toast.showError(error.message);
    })
    .finally(() => {
      reinstallInProgress.value = false;
    });
}

function save() {
  if (updateInProgress.value || !botAppLoaded.value || botAppInstallError.value) {
    return;
  }

  if (rulesList.value?.areRulesAreValid()) {
    updateInProgress.value = true;
    botsApi
      .update(currentWorkspace.value.id, viewModel.value)
      .then(() => {
        Toast.showSuccess('Successfully updated Bot');
      })
      .catch((err) => {
        Toast.showError(err.message);
      })
      .finally(() => {
        updateInProgress.value = false;
      });
  } else {
    Toast.showError('Please fix the errors displayed before saving');
  }
}

function enableBot() {
  if (enableInProgress.value) {
    return;
  }

  enableInProgress.value = true;

  // todo: make a copy of the viewModel on init and on save
  // then use that version of the viewModel in this request
  // so that other options aren't saved by enable or disable
  viewModel.value.disabled = false;

  botAppSerivce
    .enableBot(currentWorkspace.value, viewModel.value)
    .then(() => {
      Toast.showSuccess('Bots have been enabled');
      viewModel.value.disabled = false;
    })
    .catch((err) => {
      Toast.showError(err.message);
    })
    .finally(() => {
      enableInProgress.value = false;
    });
}

function disableBot() {
  if (!botAppLoaded.value) {
    return;
  }

  if (confirm('Are you sure you want to disable bots?')) {
    // see related todo in enableBot
    viewModel.value.disabled = true;

    botAppSerivce
      .disableBot(currentWorkspace.value, viewModel.value)
      .then(() => {
        Toast.showSuccess('Bots have been disabled');
        viewModel.value.disabled = true;
      })
      .catch((err) => {
        Toast.showError(err.message);
      });
  }
}

function initBotApp(config: BotConfigurationViewModel) {
  viewModel.value = config;
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.triage-contacts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  .column {
    width: 100%;
    padding: 0.75rem;
  }

  .field {
    display: flex;
    align-items: flex-start;
  }

  .contact-information {
    display: flex;
    margin-bottom: 15px;
    line-height: 24px;
    align-items: center;

    &:last-of-type {
      margin-bottom: 40px;
    }

    .topic-tags {
      width: calc(100% - 10em);
      padding: 0;
    }

    .contact-attribute-label {
      width: 12em;
    }

    label {
      height: 1.5em;
    }

    p {
      margin-left: 1em;
    }
  }
}

.header {
  padding-top: 3em;
}

///////////////////////////////

*::-webkit-scrollbar {
  width: 0.9em;
  height: 0.8em;
}

*::-webkit-scrollbar-track {
  background-color: darken(#f1f2f6, 5%);
}

*::-webkit-scrollbar-thumb {
  background-color: darken(#f1f2f6, 12%);
  border-radius: 2em;
}

.optionlist-list {
  padding: 0.5em 0;
  display: flex;
  flex-wrap: wrap;

  .no-items-text {
    opacity: 0.6;
  }

  .optionlist-item {
    background: #f1f2f6;
    padding: 0.3em 0.75em;
    border-radius: 5em;
    flex-direction: row;
    display: flex;
    align-items: center;
    margin-bottom: 0.5em;

    &:not(:last-child) {
      margin-right: 0.5em;
    }

    .delete-icon {
      width: 0.6em;
      height: 0.6em;
      cursor: pointer;
      fill: #c7cdd3;
      margin-left: 0.5em;

      &:hover {
        fill: #a4a6a9;
      }
    }
  }

  &.disabled {
    color: rgba($color: #000000, $alpha: 0.25);
    border-color: rgba($color: #000000, $alpha: 0.25);

    .optionlist-item {
      .delete-icon {
        cursor: default;
        fill-opacity: 0.5;

        &:hover {
          fill: #c7cdd3;
        }
      }
    }
  }
}

.intro-message {
  margin-bottom: 15px;
}

.banner-text {
  margin-right: 1rem;
}

.save-bot {
  display: flex;
  align-items: center;

  .save-button {
    margin-right: 1rem;
  }
}
</style>
